import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';

import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import EmbeddedChatWidget from '@bibliocommons/shared-embedded-chat-widget';
import { QuestionMarkCircled } from '@bibliocommons/deprecated-base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { libraryShape, didNotFindOptionsShape } from '@bibliocommons/bc-prop-types';

import Context from '@bibliocommons/context';
import { SAVED_SEARCH_WORKFLOW } from 'app/constants/WorkflowConstants';
import SearchTitle from '../SearchTitle';
import ShelfAvailabilityTitle from '../ShelfAvailabilityTitle';
import MobileSearchBox from '../MobileSearchBox';
import DidNotFind from '../DidNotFind';
import DidYouMean from '../DidYouMean';
import ExternalSearchLink from '../ExternalSearchLink';
import ChatWidgetButton from '../ChatWidgetButton';

import ActiveFilters from '../ActiveFilters';

import './EmptySearchResult.scss';

export class EmptySearchResult extends React.PureComponent {
  renderActiveFilters() {
    const { activeFilters, locked, singleBranch, searchType } = this.props;
    if (!activeFilters.isEmpty()) {
      return (
        <div className="active-filters">
          <FormattedMessage id="empty_search_clear_filters_info" />
          <ActiveFilters
            searchType={searchType}
            singleBranch={singleBranch}
            activeFilters={activeFilters}
            locked={locked}
            showLockSwitch={false}
          />
        </div>
      );
    }

    return null;
  }

  renderStaticEmptySearchResults() {
    const { hasStaticEmptySearchResults, intl, query } = this.props;

    if (!hasStaticEmptySearchResults) {
      return null;
    }

    const staticEmptySearchContent = intl.formatMessage({
      id: 'static_empty_search_results_page'
    });

    if (staticEmptySearchContent) {
      const InjectQueryIntoHtml = staticEmptySearchContent.replace(/{query}/gi, query);

      return (
        <div className="static-empty-search-content">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: InjectQueryIntoHtml }}
          />
        </div>
      );
    }

    return null;
  }

  renderSavedSearchLink(updateWorkflow) {
    return (
      <TextButton
        handleClick={() => updateWorkflow(SAVED_SEARCH_WORKFLOW)}
        className="save-search-link"
        data-test-id="save-search-link"
      >
        {this.props.intl.formatMessage({ id: 'save_search' })}
      </TextButton>
    );
  }

  renderSearchBox() {
    const { orderedSearchTypes, locked, query, searchType, activeFilters } = this.props;
    return (
      this.context.mobileApp && (
        <MobileSearchBox
          locked={locked}
          query={query}
          searchType={searchType}
          searchTypes={orderedSearchTypes}
          activeFilters={activeFilters}
        />
      )
    );
  }

  renderDidNotFind() {
    const { query, didNotFindOptions } = this.props;

    return (
      didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-did-not-find">
          <DidNotFind query={query} didNotFindOptions={didNotFindOptions} emptySearch />
        </div>
      )
    );
  }

  renderDidYouMean() {
    const { didYouMeanQuerySuggestion, didYouMeanResultCount } = this.props;
    return (
      <DidYouMean
        key="did-you-mean"
        emptySearch
        querySuggestion={didYouMeanQuerySuggestion}
        resultCount={didYouMeanResultCount}
      />
    );
  }

  renderExternalSearch() {
    const { query, searchType, showExternalSearchLink } = this.props;

    return showExternalSearchLink && <ExternalSearchLink query={query} searchType={searchType} formatted />;
  }

  renderEmbeddedChatWidget() {
    const { chatWidgets } = this.props;
    const supportedWidgets = ['qwidget', 'libanswers', 'libraryh3lp', 'mosio', 'refchatter'];
    const widgets = [];

    if (!chatWidgets) {
      return null;
    }
    let key = 0;
    supportedWidgets.forEach(widget => {
      if (chatWidgets.get(widget) && chatWidgets.getIn([widget, 'enabled'])) {
        widgets.push(<EmbeddedChatWidget key={key++} widget={widget} />);
      }
    });

    if (widgets.length) {
      return <div className="empty-search-embedded-chat-widgets">{widgets}</div>;
    }

    return widgets;
  }

  renderChatWidgetButton() {
    const supportedWidgets = ['qwidget', 'libanswers', 'libraryh3lp', 'mosio', 'refchatter'];
    const enabledWidget = this.props.chatWidgets.findLastKey(widget => widget.get('enabled'));

    if (enabledWidget && supportedWidgets.includes(enabledWidget)) {
      return (
        <span className="empty-search-chat-widget">
          <ChatWidgetButton widget={enabledWidget} emptySearch />
        </span>
      );
    }

    return null;
  }

  renderSearchTipsLink() {
    const { library } = this.props;
    const searchTipsLink = library.get('searchTipsLink');

    if (searchTipsLink) {
      return (
        <a className="search-tips" href={searchTipsLink} target="_blank" rel="noopener noreferrer">
          <span aria-hidden>
            <QuestionMarkCircled />
            <FormattedMessage id="search_tips_link_text" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage
              id="ada_opens_a_new_tab"
              values={{
                linkText: <FormattedMessage id="search_tips_link_text" />
              }}
            />
          </ScreenReaderMessage>
        </a>
      );
    }
    return null;
  }

  renderSubtitle() {
    const { query, shelfName } = this.props;

    if (shelfName) {
      return <FormattedMessage id="no_available_items" tagName="h4" />;
    }

    return (
      query && <FormattedMessage id="Search.empty_search" tagName="h4" values={{ query: <strong>{query}</strong> }} />
    );
  }

  renderMainContents() {
    return (
      <div className="empty-search-main-content">
        <div className="empty-search-sub-title">
          {this.renderSubtitle()}
          {this.renderDidYouMean()}
          {this.renderActiveFilters()}
          {this.renderExternalSearch()}
        </div>
        {this.renderSearchTools()}
      </div>
    );
  }

  renderSecondaryContents() {
    return (
      !this.props.didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-secondary-content">
          {this.renderStaticEmptySearchResults()}
          {this.renderEmbeddedChatWidget()}
        </div>
      )
    );
  }

  renderSearchTools() {
    return (
      this.props.didNotFindOptions.get('isEnabled') && (
        <div className="empty-search-help-tools">
          {this.renderSearchTipsLink()}
          {this.renderChatWidgetButton()}
        </div>
      )
    );
  }

  renderSearchTitleHeader() {
    const {
      query,
      titleKey,
      didYouMeanQuerySuggestion,
      didYouMeanResultCount,
      searchType,
      broadened,
      broadenedSearchType,
      showExternalSearchLink,
      rssURL,
      customEdit,
      suppress,
      didNotFindOptions,
      currentUser,
      shelfName
    } = this.props;

    if (shelfName) {
      return <ShelfAvailabilityTitle shelfName={shelfName} userId={currentUser.get('id')} />;
    }

    return (
      <SearchTitle
        library={this.props.library}
        broadened={broadened}
        broadenedSearchType={broadenedSearchType}
        customEdit={customEdit}
        didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
        didYouMeanResultCount={didYouMeanResultCount}
        showDidYouMean={false}
        id="searchTitle"
        query={query}
        searchType={searchType}
        showExternalSearchLink={showExternalSearchLink}
        suppress={suppress}
        titleKey={titleKey}
        rssURL={rssURL}
        didNotFindOptions={didNotFindOptions}
        emptySearch
      />
    );
  }

  render() {
    return (
      <div className="cp-empty-search-result">
        {this.renderSearchBox()}
        {this.renderSearchTitleHeader()}
        {this.renderMainContents()}
        {this.renderSecondaryContents()}
        {this.renderDidNotFind()}
      </div>
    );
  }
}

EmptySearchResult.displayName = 'EmptySearchResult';
EmptySearchResult.propTypes = {
  query: PropTypes.string,
  titleKey: PropTypes.string,
  activeFilters: ImmutablePropTypes.mapOf(PropTypes.object).isRequired,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object),
  currentUser: ImmutablePropTypes.map,
  orderedSearchTypes: ImmutablePropTypes.list,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,

  hasStaticEmptySearchResults: PropTypes.bool.isRequired,
  searchType: PropTypes.string,
  broadened: PropTypes.bool,
  broadenedSearchType: PropTypes.string,
  showExternalSearchLink: PropTypes.bool,
  locked: PropTypes.bool,
  rssURL: PropTypes.string,
  customEdit: PropTypes.bool,
  suppress: PropTypes.bool,
  didNotFindOptions: didNotFindOptionsShape,
  singleBranch: PropTypes.bool,
  shelfName: PropTypes.string,
  library: libraryShape
};

EmptySearchResult.contextType = Context;

export default injectIntl(EmptySearchResult);
